import React, { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Col, Row } from 'reactstrap';
import { useTranslation } from '../translations';
import { classNames } from '../utils';
import { MaterialIcon } from '.';

import './SelectBlock.scss';

interface SelectBlockItem {
  /*
   * Ignore other values we don't use.
   * Allows other keys to be present in the items object without causing a TypeError.
   */
  [key: string]: unknown;

  name: string;
  icon?: string;
  img?: string;
  id?: string;
}

interface SelectBlockProps {
  readonly items: SelectBlockItem[];
  readonly defaultSelectedIds?: number[];
  readonly textOnly?: true;
  readonly allowMultiple?: true;
  readonly ordered?: boolean;
  readonly disabled?: boolean;
  readonly max?: number | null;
  readonly onChange: (active: number[]) => void;
}

/**
 * TODO: This component should not use item indexes instead it should refer to the items directly.
 */
export const SelectBlocks: FC<SelectBlockProps> = ({
  allowMultiple,
  disabled,
  items,
  defaultSelectedIds,
  textOnly,
  ordered,
  max,
  onChange,
}) => {
  const { ts } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds ?? []);
  const updateSelectedIds = useCallback(
    (idx: number) => {
      if (disabled) {
        return;
      }

      if (allowMultiple) {
        const newList = selectedIds.includes(idx)
          ? selectedIds.filter((i) => i !== idx)
          : selectedIds.concat(idx);

        if (max && newList.length > max) {
          toast.error(ts('you_can_only_select_up', max));
          return;
        }

        setSelectedIds(newList);
      } else {
        setSelectedIds([idx]);
      }
    },
    [allowMultiple, disabled, max, selectedIds, ts],
  );

  useEffect(() => {
    if (!allowMultiple && selectedIds.length === 0) {
      return;
    }

    onChange(selectedIds);
  }, [selectedIds]);

  return (
    <Row className="select-block-container">
      {items.map((item, idx) => {
        const selected = selectedIds.indexOf(idx);
        return (
          <SelectBlock
            // Disabled, or (not selected and at limit)
            disabled={disabled || (selected === -1 && !!max && selectedIds.length >= max)}
            item={item}
            key={item.id ?? idx}
            onClick={() => {
              updateSelectedIds(idx);
            }}
            ordered={ordered}
            selected={selected}
            textOnly={textOnly}
          />
        );
      })}
    </Row>
  );
};

interface SelectBlockItemProps {
  readonly item: SelectBlockItem;
  readonly textOnly?: true;
  readonly selected: number;
  readonly ordered?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}

const SelectBlock: FC<SelectBlockItemProps> = ({
  item: { id, icon, img, name },
  textOnly,
  onClick,
  selected,
  ordered,
  disabled,
}) => {
  return (
    <Col
      className={classNames({ selected: selected !== -1, textOnly }, 'select-block')}
      id={id}
      lg={4}
      onClick={onClick}
      style={{ cursor: disabled ? 'not-allowed' : undefined }}
      xs={12}
    >
      {selected !== -1 && (
        <MaterialIcon
          className="select-block-selected"
          name={ordered ? 'circle' : 'check_circle'}
          type="success"
        />
      )}
      {ordered && selected !== -1 && (
        <div
          className="select-block-selected"
          style={{
            width: '24px',
            backgroundColor: 'transparent',
            lineHeight: '24px',
            color: 'white',
          }}
        >
          {selected + 1}
        </div>
      )}

      {icon && (
        <div className="select-block-icon">
          <MaterialIcon large name={icon} />
        </div>
      )}
      {img && (
        <div className="select-block-icon">
          <img src={img} />
        </div>
      )}
      <div className="select-block-text">{name}</div>
    </Col>
  );
};
